import React, { useRef, useEffect, useState, useCallback } from "react";
import { X, Share2, Box, Maximize, RotateCcw, Info } from "lucide-react";
import { toast } from "sonner";
import { getMediaUrl, BACKEND_URL, getShareOrigin } from "../../utils";
import IPDisclaimer from "../common/IPDisclaimer";

/* ═══════════════════════════════════════════════════════════
   AR VIEWER — Uses <model-viewer> for native AR Quick Look
   
   On iOS: Tapping "View in AR" launches Apple AR Quick Look
           (3D model placed on real surface via camera — exactly
           like scanning a QR code from ar-code.com)
   On Android: Launches Google Scene Viewer
   On Web: Falls back to WebXR if available
   ═══════════════════════════════════════════════════════════ */
const ARViewer = ({ item, onClose, simulate = false }) => {
  const viewerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadProgress, setLoadProgress] = useState(0);
  const [arSupported, setArSupported] = useState(false);
  const [shareMsg, setShareMsg] = useState(null);

  const imgUrl = getMediaUrl(item?.image_url || item?.card_images?.[0]);
  const cardName = item?.name || "AR Card";
  const rarity = (item?.rarity || "COMMON").toUpperCase();
  const cardId = item?.id || item?._id || "";

  // 3D model URL — MUST be absolute HTTPS for AR Quick Look to work on iOS
  // AR Quick Look can't access capacitor://localhost files, needs a real server URL
  // Base URL WITHOUT /api suffix — used for model URLs and Quick Look pages
  const baseUrl = BACKEND_URL || window.location.origin;
  // Cache-bust every model URL so iOS AR Quick Look always pulls the rescaled
  // file (iOS aggressively caches .usdz files by URL).
  const _cb = `v=${Date.now()}`;
  const _bust = (u) => (u.includes("?") ? `${u}&${_cb}` : `${u}?${_cb}`);
  const modelGlb = _bust(item?.model_url_glb
    ? (item.model_url_glb.startsWith("http") ? item.model_url_glb : `${baseUrl}${item.model_url_glb}`)
    : `https://media.blkpacks.fun/ar-models/robot-expressive.glb`);
  const modelUsdz = item?.model_url_usdz
    ? _bust(item.model_url_usdz.startsWith("http") ? item.model_url_usdz : `${baseUrl}${item.model_url_usdz}`)
    : null;

  const rarBg = rarity === "LEGENDARY" ? "bg-amber-500/90 text-black" : rarity === "EPIC" ? "bg-amber-500/90 text-white" : rarity === "RARE" ? "bg-blue-500/90 text-white" : "bg-gray-600/90 text-white";
  const rarGlow = rarity === "LEGENDARY" ? "rgba(251,191,36,0.4)" : rarity === "EPIC" ? "rgba(245,158,11,0.4)" : rarity === "RARE" ? "rgba(59,130,246,0.4)" : "rgba(249,115,22,0.4)";

  /* Track AR view */
  useEffect(() => {
    const startTime = Date.now();
    const track = async () => {
      try {
        await fetch(`${baseUrl}/api/ar/track-view`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            card_id: cardId,
            card_name: cardName,
            device_type: /iPhone|iPad/.test(navigator.userAgent) ? "ios" : /Android/.test(navigator.userAgent) ? "android" : "web",
            effect_mode: "AR_QUICKLOOK",
            duration_seconds: 0,
          }),
        });
      } catch (_) {}
    };
    track();
    return () => {
      const dur = Math.round((Date.now() - startTime) / 1000);
      fetch(`${baseUrl}/api/ar/track-view`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ card_id: cardId, card_name: cardName, effect_mode: "AR_QUICKLOOK", duration_seconds: dur }),
      }).catch(() => {});
    };
  }, [cardId, cardName]);

  /* Listen for model-viewer events */
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    
    const onLoad = () => { setIsLoaded(true); setLoadProgress(1); };
    const onProgress = (e) => {
      const p = e?.detail?.totalProgress;
      if (typeof p === "number") setLoadProgress(p);
    };
    const onArStatus = (e) => {
      if (e.detail.status === "session-started") {
        toast.success("AR session started — place the model on a surface!");
      }
    };

    viewer.addEventListener("load", onLoad);
    viewer.addEventListener("progress", onProgress);
    viewer.addEventListener("ar-status", onArStatus);
    
    // Check AR support
    if (viewer.canActivateAR) {
      setArSupported(true);
    } else {
      // model-viewer may not have loaded yet, check after a delay
      const timer = setTimeout(() => {
        if (viewerRef.current?.canActivateAR) setArSupported(true);
      }, 2000);
      return () => {
        clearTimeout(timer);
        viewer.removeEventListener("load", onLoad);
        viewer.removeEventListener("progress", onProgress);
        viewer.removeEventListener("ar-status", onArStatus);
      };
    }

    return () => {
      viewer.removeEventListener("load", onLoad);
      viewer.removeEventListener("progress", onProgress);
      viewer.removeEventListener("ar-status", onArStatus);
    };
  }, []);

  /* Share */
  const handleShare = async () => {
    const shareUrl = `${getShareOrigin()}/ar/view/${cardId}`;
    const shareData = { title: `${cardName} — BLK PACKS AR`, text: `Check out this AR card: ${cardName}`, url: shareUrl };
    try {
      if (navigator.share) { await navigator.share(shareData); }
      else { await navigator.clipboard.writeText(shareUrl); setShareMsg("Link copied!"); setTimeout(() => setShareMsg(null), 2000); }
    } catch (_) {
      try { await navigator.clipboard.writeText(shareUrl); setShareMsg("Link copied!"); setTimeout(() => setShareMsg(null), 2000); } catch (_2) {}
    }
  };

  /* Close */
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  /* ESC / hardware back closes AR viewer */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose]);

  /* Activate AR — opens in-app Safari on iOS (WKWebView can't do AR Quick Look) */
  const activateAR = async () => {
    const quickLookUrl = `${baseUrl}/api/ar/quicklook/${cardId || "demo"}`;
    
    // Check if we're in a Capacitor native app
    const isNative = window.Capacitor?.isNativePlatform?.() || false;
    
    if (isNative) {
      try {
        // Open in SFSafariViewController — this supports AR Quick Look on iOS
        const { Browser } = await import("@capacitor/browser");
        await Browser.open({ url: quickLookUrl, presentationStyle: "fullscreen" });
        return;
      } catch (err) {
        console.warn("[AR] Browser plugin failed, trying window.open:", err);
        // Fallback: open in external Safari
        window.open(quickLookUrl, "_system");
        return;
      }
    }
    
    // On web: try model-viewer's built-in AR, or open Quick Look page in new tab
    if (viewerRef.current?.activateAR) {
      viewerRef.current.activateAR();
    } else {
      window.open(quickLookUrl, "_blank");
    }
  };

  /* Reset camera */
  const resetCamera = () => {
    if (viewerRef.current) {
      viewerRef.current.cameraOrbit = "auto auto auto";
      viewerRef.current.cameraTarget = "auto auto auto";
      viewerRef.current.fieldOfView = "auto";
    }
  };

  return (
    <div className="fixed inset-0 z-[99999] bg-[#050507]" data-testid="ar-viewer">
      {/* model-viewer fills the screen */}
      <model-viewer
        ref={viewerRef}
        src={modelGlb}
        {...(modelUsdz ? { "ios-src": modelUsdz } : {})}
        ar
        ar-modes="webxr scene-viewer quick-look"
        ar-scale="auto"
        ar-placement="floor"
        camera-controls
        touch-action="pan-y"
        auto-rotate
        autoplay
        shadow-intensity="1.2"
        environment-image="neutral"
        exposure="1.1"
        camera-orbit="0deg 75deg 200%"
        min-camera-orbit="auto auto 100%"
        max-camera-orbit="auto auto 500%"
        field-of-view="25deg"
        poster={imgUrl || "auto"}
        alt={`${cardName} 3D AR Model`}
        loading="eager"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
          background: `radial-gradient(ellipse at center, ${rarGlow} 0%, #050507 70%)`,
          "--poster-color": "#050507",
        }}
      >
        {/* Custom AR button (hidden — we use our own) */}
        <button slot="ar-button" style={{ display: "none" }} data-testid="model-viewer-ar-btn">AR</button>
      </model-viewer>

      {/* UI Overlay - MUST be z-above model-viewer and force pointer events */}
      <div className="absolute inset-0 z-[100] pointer-events-none flex flex-col">
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 pointer-events-auto" style={{ paddingTop: "max(env(safe-area-inset-top, 12px), 48px)" }}>
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClose(); }}
            onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleClose(); }}
            className="w-12 h-12 rounded-full bg-black/80 backdrop-blur-sm flex items-center justify-center active:scale-95 transition-transform"
            style={{ touchAction: "manipulation", zIndex: 101, position: "relative" }}
            data-testid="ar-close-btn"
            aria-label="Close AR viewer"
          >
            <X size={22} className="text-white" />
          </button>
          <div className="bg-black/60 backdrop-blur-sm rounded-full px-3 py-1.5 flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isLoaded ? "bg-green-400" : "bg-yellow-400"} animate-pulse`} />
            <span className="text-white text-xs font-bold tracking-wider">
              {isLoaded ? "3D READY" : "LOADING"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleShare} className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm flex items-center justify-center active:scale-95 transition-transform" data-testid="ar-share-btn">
              <Share2 size={18} className="text-white" />
            </button>
          </div>
        </div>

        {/* Share toast */}
        {shareMsg && (
          <div className="flex justify-center mt-2 pointer-events-none">
            <div className="bg-green-500/90 backdrop-blur-sm text-white text-sm font-bold px-4 py-2 rounded-full">{shareMsg}</div>
          </div>
        )}

        {/* Loading overlay with progress + poster */}
        {!isLoaded && (
          <div className="absolute inset-0 z-30 pointer-events-none">
            {/* Card poster — fades out as model loads */}
            {imgUrl && (
              <img
                src={imgUrl}
                alt={cardName}
                className="absolute inset-0 w-full h-full object-contain opacity-70"
                style={{ transition: "opacity 300ms" }}
              />
            )}
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center">
              <div className="w-12 h-12 border-[3px] border-orange-500/30 border-t-orange-500 rounded-full animate-spin mb-4" />
              <p className="text-white font-bold text-base">Loading 3D Model</p>
              <p className="text-amber-300 text-xs mt-1 font-mono tabular-nums">
                {Math.round(loadProgress * 100)}%
              </p>
              <div className="w-48 h-1 mt-3 bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-amber-400 transition-all duration-300"
                  style={{ width: `${Math.round(loadProgress * 100)}%` }}
                />
              </div>
              <p className="text-gray-500 text-[10px] mt-3">Large 3D models may take a few seconds on cellular</p>
            </div>
          </div>
        )}

        {/* Spacer to push bottom content down */}
        <div className="flex-1" />

        {/* Bottom area */}
        <div className="pointer-events-auto" style={{ paddingBottom: "max(env(safe-area-inset-bottom, 8px), 24px)" }}>
          <div className="bg-gradient-to-t from-black/90 via-black/60 to-transparent pt-20 pb-4 px-4">
            {/* AR + Controls row */}
            <div className="flex items-center gap-2 mb-4">
              {/* View in AR — the main action */}
              <button
                onClick={activateAR}
                className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-amber-400 hover:to-amber-400 rounded-full py-3.5 text-white font-bold text-sm flex items-center justify-center gap-2 shadow-[0_4px_20px_rgba(249,115,22,0.4)] active:scale-[0.97] transition-all"
                data-testid="ar-launch-btn"
              >
                <Maximize size={18} />
                View in AR
              </button>
              <button onClick={resetCamera} className="w-11 h-11 rounded-full bg-white/10 border border-white/20 flex items-center justify-center active:scale-95 transition-transform" data-testid="ar-reset-btn">
                <RotateCcw size={16} className="text-white/70" />
              </button>
            </div>

            {/* Card info */}
            <div className="flex items-center gap-2 mb-1">
              <span className="text-white font-black text-[10px] tracking-[0.12em]" style={{ textShadow: `0 0 6px ${rarGlow}` }}>BLK</span>
              <span className="text-amber-400/50 text-[7px] font-bold tracking-wider">PACKS</span>
              <span className="bg-orange-500/90 text-white text-[7px] font-bold px-1.5 py-0.5 rounded-full">AR</span>
              <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded-full ${rarBg}`}>{rarity}</span>
            </div>
            <h2 className="text-white font-black text-xl uppercase tracking-tight">{cardName}</h2>
            {item?.edition_number && <p className="text-gray-400 text-xs mt-0.5">#{item.edition_number} · {item.series || "Collection"}</p>}
            
            {/* Instructions */}
            <div className="flex items-start gap-2 mt-3 bg-white/[0.04] border border-white/[0.08] rounded-xl px-3 py-2.5">
              <Info size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-gray-400 text-[11px] leading-relaxed">
                Tap <strong className="text-white">"View in AR"</strong> to place this 3D model on a real surface through your camera. 
                Pinch to resize, drag to move. Works on iPhone and Android.
              </p>
            </div>
            {/* IP / Software disclaimer — explicit at AR launch */}
            <IPDisclaimer variant="card" className="mt-3" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ARViewer;
